/**
 * Compact chip that narrates a single agent tool invocation ("Consultando
 * agenda…", "Criando lembrete…").
 *
 * Renders from a `MessagePart` of type `toolCall` / `toolResult`. Expandable to
 * reveal the raw args/result for transparency (the firm values auditability),
 * but collapsed and quiet by default.
 */
import type { AgentToolName, MessagePart } from '@agente/shared'
import {
  Bell,
  BookMarked,
  CalendarDays,
  CalendarPlus,
  ChevronRight,
  Contact,
  Globe,
  Inbox,
  Link2,
  ListChecks,
  Mail,
  MailSearch,
  MessageCircle,
  NotebookPen,
  Presentation,
  Reply,
  Send,
  User,
  UserSearch,
  Users,
  Wrench,
} from 'lucide-react'
import { type ComponentType, useState } from 'react'

import { SourceLink, webSearchResultData } from '@/components/chat/search-sources'
import { cn } from '@/lib/utils'

interface ToolMeta {
  label: string
  icon: ComponentType<{ className?: string }>
}

const TOOL_META: Record<AgentToolName, ToolMeta> = {
  getCalendar: { label: 'Consultando a agenda', icon: CalendarDays },
  createReminder: { label: 'Criando lembrete', icon: Bell },
  listReminders: { label: 'Verificando lembretes', icon: ListChecks },
  sendTeamsMessage: { label: 'Enviando mensagem no Teams', icon: Send },
  getClientProfile: { label: 'Consultando perfil do cliente', icon: User },
  upsertClientProfile: { label: 'Atualizando perfil do cliente', icon: Users },
  webSearch: { label: 'Pesquisando na web', icon: Globe },
  searchMemories: { label: 'Consultando memórias', icon: BookMarked },
  saveMemory: { label: 'Salvando memória', icon: NotebookPen },
  completeOnboarding: { label: 'Concluindo a apresentação', icon: Presentation },
  startEmailResearch: { label: 'Pesquisando nos e-mails', icon: MailSearch },
  searchEmails: { label: 'Buscando e-mails', icon: Inbox },
  sendEmail: { label: 'Enviando e-mail', icon: Mail },
  replyEmail: { label: 'Respondendo e-mail', icon: Reply },
  createEvent: { label: 'Criando evento na agenda', icon: CalendarPlus },
  searchTenantUsers: { label: 'Pesquisando pessoas', icon: UserSearch },
  listConnectedAccounts: { label: 'Consultando contas conectadas', icon: Link2 },
  sendWhatsappMessage: { label: 'Enviando mensagem no WhatsApp (beta)', icon: MessageCircle },
  sendWhatsappTemplateToContact: {
    label: 'Enviando WhatsApp para contato (beta)',
    icon: Contact,
  },
}

function metaFor(toolName?: string): ToolMeta {
  if (toolName && toolName in TOOL_META) {
    return TOOL_META[toolName as AgentToolName]
  }
  return { label: toolName ? `Ferramenta: ${toolName}` : 'Ferramenta', icon: Wrench }
}

/** pt-BR label for a tool invocation (also used by the voice session overlay). */
export function agentToolLabel(toolName?: string): string {
  return metaFor(toolName).label
}

export function ToolChip({ part }: { part: MessagePart }) {
  const [open, setOpen] = useState(false)
  const { label, icon: Icon } = metaFor(part.toolName)
  const webSearch = webSearchResultData(part)
  const hasDetail =
    (part.toolArgs && Object.keys(part.toolArgs).length > 0) || part.toolResult !== undefined

  return (
    <div className="border-border/70 bg-muted/40 rounded-lg border text-xs">
      <button
        type="button"
        disabled={!hasDetail}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={hasDetail ? open : undefined}
        className={cn(
          'flex w-full items-center gap-2 px-2.5 py-1.5 text-left',
          hasDetail && 'hover:bg-muted/70 rounded-lg transition-colors',
        )}
      >
        <Icon className="text-muted-foreground size-3.5 shrink-0" />
        <span className="text-foreground/90 font-medium">{label}</span>
        {hasDetail ? (
          <ChevronRight
            className={cn(
              'text-muted-foreground ml-auto size-3.5 shrink-0 transition-transform',
              open && 'rotate-90',
            )}
          />
        ) : null}
      </button>
      {open && hasDetail ? (
        <div className="border-border/70 space-y-2 border-t px-2.5 py-2">
          {part.toolArgs && Object.keys(part.toolArgs).length > 0 ? (
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Parâmetros</p>
              <pre className="text-muted-foreground overflow-x-auto font-mono text-[0.6875rem] leading-snug">
                {JSON.stringify(part.toolArgs, null, 2)}
              </pre>
            </div>
          ) : null}
          {webSearch && webSearch.sources.length > 0 ? (
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Fontes</p>
              <ul className="flex flex-wrap gap-1.5">
                {webSearch.sources.map((source) => (
                  <li key={source.url}>
                    <SourceLink source={source} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {part.toolResult !== undefined ? (
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Resultado</p>
              <pre className="text-muted-foreground overflow-x-auto font-mono text-[0.6875rem] leading-snug">
                {JSON.stringify(part.toolResult, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
